import React from "react";
import { NeoBadge } from "../../../../../components/Common";
import { calculateRunningBlockSummary } from "../../domain/running/calculations";
import {
    RUNNING_INTENSITY_ZONES,
    RUNNING_SEGMENT_TYPES,
    getRunningLabel,
} from "../../domain/running/constants";

const getZone = (value) => RUNNING_INTENSITY_ZONES.find(zone => zone.value === value);

const getSegmentLabel = (segment = {}) =>
    segment.label || getRunningLabel(RUNNING_SEGMENT_TYPES, segment.segmentType);

const formatSegmentTarget = (segment = {}) => {
    if (segment.targetType === "pace") {
        if (segment.targetPaceMin || segment.targetPaceMax) {
            return `${segment.targetPaceMin || "-"} a ${segment.targetPaceMax || "-"}/km`;
        }
        return "Pace por grupo";
    }

    if (segment.targetType === "heart_rate") return segment.heartRateZone || "FC";
    if (segment.targetType === "power") return segment.powerZone || "Potência";
    if (segment.targetType === "cadence") return segment.cadenceTarget || "Cadência";
    if (segment.targetType === "effort") return getZone(segment.effortZone)?.desc || "Esforço";
    return "Livre";
};

const formatSegmentDuration = (segment = {}) => {
    const reps = Number(segment.reps || 1);
    const prefix = reps > 1 ? `${reps}x ` : "";

    if (segment.durationType === "time") return `${prefix}${segment.duration || "-"} min`;
    if (segment.durationType === "distance") return `${prefix}${segment.distance || "-"} ${segment.distanceUnit || "km"}`;
    if (segment.durationType === "open") return `${prefix}livre`;
    return `${prefix}${segment.durationType || "livre"}`;
};

const getGroupTarget = (segment = {}, group = {}, index = 0) => {
    const groupKey = group.id || group.value || `group_${index}`;
    return segment.paceGroupTargetsById?.[groupKey] || {};
};

const RunningCoachBrief = ({ workout = {} }) => {
    const clubSession = workout.clubSession || {};
    const paceGroups = (clubSession.paceGroups || []).filter(group => group.enabled !== false);
    const blocks = workout.blocks || [];

    if (!blocks.length) return null;

    return (
        <div className="running-coach-brief mb-3">
            <div className="running-coach-brief-header">
                <div>
                    <span>Modo Professor</span>
                    <strong>{workout.name || "Sessão de corrida"}</strong>
                </div>
                {clubSession.isClubSession && (
                    <NeoBadge color="secondary" className="fw-semibold">
                        {clubSession.teamName || "Treino de clube"}
                    </NeoBadge>
                )}
            </div>

            {clubSession.isClubSession && (
                <div className="running-coach-logistics">
                    <span><strong>Horário:</strong> {clubSession.startTime || "-"}</span>
                    <span><strong>Encontro:</strong> {clubSession.meetingPoint || "-"}</span>
                    <span><strong>Rota:</strong> {clubSession.routeName || "-"}</span>
                    <span><strong>Professor:</strong> {clubSession.coachName || "-"}</span>
                    {clubSession.safetyNotes && (
                        <span className="running-coach-logistics-note"><strong>Segurança:</strong> {clubSession.safetyNotes}</span>
                    )}
                </div>
            )}

            {workout.cyclePlan?.enabled && (
                <div className="running-coach-planning">
                    <span>
                        <strong>Ciclo:</strong> {workout.cyclePlan.raceName || "Objetivo"} • Semana {workout.cyclePlan.weekNumber || "-"} de {workout.cyclePlan.totalWeeks || "-"} • {workout.cyclePlan.plannedWeeklyKm || "-"} km/semana
                    </span>
                </div>
            )}

            {paceGroups.length > 0 && (
                <div className="running-coach-pace-strip">
                    {paceGroups.map(group => (
                        <div key={group.id || group.value}>
                            <strong>{group.label}</strong>
                            <span>{group.desc}</span>
                            <small>Leve {group.easyPace || "-"} • Tiros {group.intervalPace || "-"}</small>
                        </div>
                    ))}
                </div>
            )}

            <div className="running-coach-block-list">
                {blocks.map((block, blockIndex) => {
                    const summary = calculateRunningBlockSummary(block);
                    return (
                        <div key={block.id || blockIndex} className="running-coach-block">
                            <div className="running-coach-block-title">
                                <strong>{blockIndex + 1}. {block.name || "Bloco"}</strong>
                                <span>{summary.repeats}x • {summary.distanceKm.toFixed(1).replace(".", ",")} km</span>
                            </div>
                            {block.description && <p>{block.description}</p>}
                            <div className="running-coach-segment-list">
                                {(block.sets || []).map((segment, segmentIndex) => {
                                    const zone = getZone(segment.effortZone);
                                    const hasGroupTargets = segment.targetType === "pace" && paceGroups.length > 0;
                                    return (
                                        <div key={segment.id || segmentIndex} className="running-coach-segment">
                                            <div className="running-coach-segment-main">
                                                <span className="running-coach-segment-count">{formatSegmentDuration(segment)}</span>
                                                <strong>{getSegmentLabel(segment)}</strong>
                                                <span>{formatSegmentTarget(segment)}</span>
                                                {zone && <em style={{ "--running-zone-color": zone.color }}>{zone.label}</em>}
                                            </div>
                                            {segment.restAfter && <small>Recuperação: {segment.restAfter}</small>}
                                            {segment.notes && <small>{segment.notes}</small>}
                                            {hasGroupTargets && (
                                                <div className="running-coach-group-targets">
                                                    {paceGroups.map((group, groupIndex) => {
                                                        const target = getGroupTarget(segment, group, groupIndex);
                                                        const pace = target.targetPaceMin || target.targetPaceMax
                                                            ? `${target.targetPaceMin || "-"} a ${target.targetPaceMax || "-"}`
                                                            : group.intervalPace || group.thresholdPace || group.easyPace || "-";
                                                        return (
                                                            <span key={group.id || group.value || groupIndex}>
                                                                <strong>{group.label}</strong> {pace}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {block.restBetweenRepeats && (
                                <div className="running-coach-block-rest">
                                    Recuperação entre repetições: {block.restBetweenRepeats}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RunningCoachBrief;
